import { useRef, useState } from "react";
import { Camera, Trash2, Loader2 } from "lucide-react";
import { uploadMyAvatar, removeMyAvatar, apiErrorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const API = process.env.REACT_APP_BACKEND_URL;
export const avatarSrc = (url) => (url ? (url.startsWith("http") ? url : `${API}${url}`) : null);

export function StaffAvatar({ name, avatarUrl, size = 36, className = "", testid = "staff-avatar" }) {
  const [broken, setBroken] = useState(false);
  const src = !broken && avatarSrc(avatarUrl);
  return src ? (
    <img data-testid={testid} src={src} alt={name || ""} onError={() => setBroken(true)} width={size} height={size} className={cn("rounded-full object-cover shrink-0 border-2 border-white shadow-sm", className)} style={{ width: size, height: size }} />
  ) : (
    <span data-testid={testid} className={cn("grid place-items-center rounded-full bg-[#0038EB] text-white font-bold shrink-0", className)} style={{ width: size, height: size, fontSize: Math.max(11, size * 0.4) }}>{(name || "?").trim().slice(0, 1).toUpperCase()}</span>
  );
}

export function AvatarUploader({ labels, size = 72, compact = false }) {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ref = useRef(null);
  const run = async (fn) => { setBusy(true); setErr(""); try { await fn(); await refreshUser(); } catch (e) { setErr(apiErrorMessage(e)); } finally { setBusy(false); } };
  const pick = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) run(() => uploadMyAvatar(f)); };
  return (
    <div className={cn("flex items-center gap-3", compact ? "" : "flex-wrap")} data-testid="avatar-uploader">
      <StaffAvatar name={user?.name} avatarUrl={user?.avatar_url} size={size} testid="avatar-uploader-preview" />
      <div className="space-y-1.5 min-w-0">
        <div className="flex gap-2 flex-wrap">
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid="avatar-file-input" />
          <button type="button" data-testid="avatar-upload-btn" disabled={busy} onClick={() => ref.current?.click()} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#0038EB] text-white text-sm font-semibold disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} {user?.avatar_url ? labels.change : labels.upload}</button>
          {user?.avatar_url && <button type="button" data-testid="avatar-remove-btn" disabled={busy} onClick={() => run(removeMyAvatar)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-rose-600 text-sm font-semibold"><Trash2 size={14} /> {labels.remove}</button>}
        </div>
        <p className="text-xs text-slate-400">{labels.hint}</p>
        {err && <p data-testid="avatar-error" className="text-xs font-semibold text-rose-600">{err}</p>}
      </div>
    </div>
  );
}
